/**
 * 확인 필요 항목. 일반 저장 전에 사람이 볼 자리를 칸 묶음마다 한 줄로 보인다.
 *
 * 두 가지를 모은다.
 * - 빈 필수 칸. 판정은 `mappingRequired.ts`(BE `incomplete()` 와 같은 판정)다. 여기서는 묶고 이름만 붙인다.
 * - 이 episode 와 맞지 않는 참조. 필수 규칙이 아니다. 매핑의 카메라 원본 · 채널 · 열이 이 episode 에 없는 경우다.
 *
 * 묶음은 폼의 칸(손 × 역할 × 칸)과 카메라 줄이다. 폼에 없는 칸(`*.basis` · `motion` · 모델 · step rate)은 손 × 역할이나
 * 최상위 키로 묶고, 누르면 JSON 탭으로 간다. 줄 순서는 문서 순서다. episode 참조만 있는 줄은 뒤에 온다.
 *
 * Owns:
 * - 묶음, 이동 대상, 설명 문장.
 *
 * Does not own:
 * - 필수 판정 — `mappingRequired.ts`. 경로 이름 — `mappingPath.ts`. 수치 해석 — 서버 검증.
 */
import type { EpisodeInspect } from "../domain/episodeInspect";
import { HAND_ROLE_NAMES, isSelectionComplete, type Mapping } from "../domain/mapping";
import { issueTarget, type IssueTarget } from "./issuePath";
import type { MappingEditorState } from "./mappingEditor";
import { describeMappingPath } from "./mappingPath";
import { requiredIssues } from "./mappingRequired";
import { sameMapping } from "./mappingEdit";

/** 항목을 눌렀을 때 갈 자리. issue 목록의 `수정` 과 같은 자리다(`issueTarget`). */
export type MappingReviewTarget = IssueTarget;

export type MappingReviewItem = {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly target: MappingReviewTarget;
};

const NEEDS_ONE_DETAIL: Readonly<Record<string, string>> = {
  "hands.invalid": "손을 아직 선택하지 않았습니다. 손 구성을 골라 주세요.",
  "cameras.invalid": "카메라를 연결해 주세요.",
};
const ABSENT_CAMERA = "이 episode 에 없는 카메라입니다.";
const ABSENT_SELECTION = "이 episode 에 없는 채널 또는 열입니다.";

type GroupKey = {
  readonly id: string;
  readonly label: string;
  readonly target: MappingReviewTarget;
};

type Group = GroupKey & {
  /** 빈 칸의 이름. 묶음 자신이 빈 칸이면 `null` 이다. */
  readonly empty: (string | null)[];
  readonly notes: string[];
};

/** 묶음 경로 아래의 이름. 예) 묶음 `hands.0.observation.position` 의 `….unit` → `단위`. */
function nameWithin(draft: Mapping, group: string, path: string): string | null {
  if (path === group) {
    return null;
  }
  const head = describeMappingPath(draft, group);
  const full = describeMappingPath(draft, path);
  return full.startsWith(`${head} `) ? full.slice(head.length + 1) : full;
}

/**
 * 경로 하나가 들어갈 묶음과 그 안의 이름. 고칠 자리는 `issueTarget` 이 정한다(issue 목록의 `수정` 과 같다).
 * 폼의 칸은 손 × 역할 × 칸, 카메라 줄로 묶는다. 폼에 없는 칸은 손 × 역할이나 최상위 키로 묶고 이름은 JSON 키 그대로다.
 */
function place(draft: Mapping, path: string): { key: GroupKey; name: string | null } {
  const target = issueTarget(path, draft) ?? { kind: "json" };
  const [head = path, index, role] = path.split(".");
  const roleGroup = head === "hands" && index !== undefined && role !== undefined;
  if (target.kind === "json") {
    const group = roleGroup ? `hands.${index}.${role}` : head;
    return {
      key: { id: `json:${group}`, label: describeMappingPath(draft, group), target },
      name: path === group ? null : path.slice(group.length + 1),
    };
  }
  const group =
    target.field === "cameras"
      ? index === undefined
        ? head
        : `cameras.${index}`
      : !roleGroup
        ? head
        : target.field === "hands"
          ? `hands.${index}.${role}`
          : `hands.${index}.${role}.${target.field}`;
  return {
    key: { id: group, label: describeMappingPath(draft, group), target },
    name: nameWithin(draft, group, path),
  };
}

function describe(group: Group): string {
  const filled = group.empty.filter((name): name is string => name !== null);
  const sentences: string[] = [];
  if (group.empty.length > 0) {
    if (group.target.kind === "json") {
      sentences.push(
        filled.length === 0
          ? "JSON 탭에서 값을 입력해 주세요."
          : `JSON 탭에서 ${filled.join(" · ")} 칸을 입력해 주세요.`,
      );
    } else if (filled.length === 0) {
      sentences.push("값을 채워 주세요.");
    } else {
      sentences.push(`${filled.join(" · ")} 칸을 채워 주세요.`);
    }
  }
  return [...sentences, ...group.notes].join(" ");
}

/** 빈 필수 칸과 이 episode 에 없는 참조를 칸 묶음마다 한 줄로 안내한다. */
export function mappingReviewItems(
  state: Pick<MappingEditorState, "draft">,
  inspect: EpisodeInspect,
): MappingReviewItem[] {
  const { draft } = state;
  const groups = new Map<string, Group>();
  const groupFor = (key: GroupKey): Group => {
    let group = groups.get(key.id);
    if (group === undefined) {
      group = { ...key, empty: [], notes: [] };
      groups.set(key.id, group);
    }
    return group;
  };

  for (const issue of requiredIssues(draft)) {
    const needsOne = NEEDS_ONE_DETAIL[issue.code];
    if (needsOne !== undefined) {
      groups.set(issue.path, {
        id: issue.path,
        label: describeMappingPath(draft, issue.path),
        target: { kind: "form", field: issue.path === "hands" ? "hands" : "cameras" },
        empty: [],
        notes: [needsOne],
      });
      continue;
    }
    const { key, name } = place(draft, issue.path);
    groupFor(key).empty.push(name);
  }

  const videoKeys = new Set(inspect.videos.map((video) => video.key));
  draft.cameras.forEach((camera, index) => {
    if (camera.source !== null && !videoKeys.has(camera.source)) {
      groupFor(place(draft, `cameras.${index}`).key).notes.push(ABSENT_CAMERA);
    }
  });
  draft.hands.forEach((hand, handIndex) => {
    for (const role of HAND_ROLE_NAMES) {
      const value = hand[role];
      // action 이 없으면 내보내지 않는다는 뜻이다. 안내할 것이 없다.
      if (value === null) continue;
      for (const field of ["position", "orientation", "gripper"] as const) {
        const source = value[field].source;
        if (!isSelectionComplete(source)) continue;
        const channel = inspect.channels.find((item) => item.key === source.channel);
        const absent =
          channel === undefined ||
          source.columns.some((column) => column !== null && column >= channel.dim);
        if (absent) {
          groupFor(place(draft, `hands.${handIndex}.${role}.${field}`).key).notes.push(
            ABSENT_SELECTION,
          );
        }
      }
    }
  });

  return [...groups.values()].map((group) => ({
    id: group.id,
    label: group.label,
    detail: describe(group),
    target: group.target,
  }));
}

export function validationSummary(state: MappingEditorState, probeUuid: string): string {
  const validation = state.validation;
  if (validation.status === "running") return "검증 중";
  if (validation.status === "failed") return "검증 실패";
  if (validation.status !== "done") return "미검증";
  if (!sameMapping(validation.sent, state.draft) || validation.result.probeUuid !== probeUuid)
    return "오래된 결과";
  const passed = validation.result.episodes.filter((episode) => episode.valid).length;
  return `${passed}/${validation.result.episodes.length} episode 통과`;
}
